import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, User, Mail, Phone, DollarSign, Calendar, FileText } from 'lucide-react';

const AddStaffModal = ({ isOpen, onClose, onSubmit, userProfile, onStaffAdded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    phone_number: '',
    emergency_contact: '',
    staff_role: 'check_in_staff',
    hourly_rate: '',
    hire_date: new Date()?.toISOString()?.split('T')?.[0],
    notes: '',
    skills: [],
    status: 'active',
    password: '', // New password field
    confirmPassword: '' // New confirm password field
  });

  const [skillInput, setSkillInput] = useState('');

  const availableSkills = [
    'customer_service',
    'registration',
    'security',
    'crowd_control',
    'technical_support',
    'event_coordination',
    'catering',
    'cleaning',
    'setup_teardown',
    'parking_management',
    'ushering',
    'first_aid'
  ];

  // Add state for staff role options
  const [staffRoleOptions, setStaffRoleOptions] = useState([
    { value: 'check_in_staff', label: 'Check-in Staff' },
    { value: 'security_staff', label: 'Security Staff' },
    { value: 'technical_staff', label: 'Technical Staff' },
    { value: 'customer_service_staff', label: 'Customer Service Staff' },
    { value: 'coordinator_staff', label: 'Coordinator Staff' },
    { value: 'supervisor_staff', label: 'Supervisor Staff' }
  ]);

  // Load staff role options on component mount
  useEffect(() => {
    const loadStaffRoleOptions = async () => {
      // Add staffService declaration or remove this call since it's not defined // For now, we'll comment out the undefined staffService call
      /*
      const { data, error } = await staffService?.getStaffRoleOptions?.();
      if (data && !error) {
        const options = data?.map(role => ({
          value: role,
          label: role?.split('_')?.map(word => 
            word?.charAt(0)?.toUpperCase() + word?.slice(1)
          )?.join(' ')
        }));
        setStaffRoleOptions(options);
      }
      */
    };

    loadStaffRoleOptions();
  }, []);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event?.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Handle outside click
  const handleBackdropClick = (event) => {
    if (modalRef?.current && !modalRef?.current?.contains(event?.target)) {
      onClose();
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  // Handle skill addition
  const addSkill = (skill) => {
    if (!formData?.skills?.includes(skill)) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev?.skills, skill]
      }));
    }
  };

  // Handle skill removal
  const removeSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev?.skills?.filter(s => s !== skill)
    }));
  };

  // Add custom skill
  const handleAddCustomSkill = () => {
    const skill = skillInput?.trim()?.toLowerCase()?.replace(/\s+/g, '_');
    if (skill && !formData?.skills?.includes(skill)) {
      addSkill(skill);
      setSkillInput('');
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.staff_role) {
      newErrors.staff_role = 'Staff role is required';
    }

    // Password validation
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm the password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setError(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Use the new createStaffWithPassword method
      const { data, error } = await onSubmit(formData, formData?.password, userProfile);

      if (error) {
        setError({ general: error });
        return;
      }

      onStaffAdded?.(data);
      onClose?.();
    } catch (error) {
      setError({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleBackdropClick}
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e?.stopPropagation()} // Prevent click from bubbling to backdrop
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <Plus className="w-5 h-5 text-blue-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Add New Staff Member</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                {typeof error === 'string' ? (
                  <p className="text-red-700 text-sm">{error}</p>
                ) : (
                  <div className="space-y-1">
                    {error?.general && (
                      <p className="text-red-700 text-sm">{error?.general}</p>
                    )}
                    {error?.full_name && (
                      <p className="text-red-700 text-sm">• {error?.full_name}</p>
                    )}
                    {error?.email && (
                      <p className="text-red-700 text-sm">• {error?.email}</p>
                    )}
                    {error?.staff_role && (
                      <p className="text-red-700 text-sm">• {error?.staff_role}</p>
                    )}
                    {error?.password && (
                      <p className="text-red-700 text-sm">• {error?.password}</p>
                    )}
                    {error?.confirmPassword && (
                      <p className="text-red-700 text-sm">• {error?.confirmPassword}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Personal Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData?.full_name}
                      onChange={(e) => handleChange('full_name', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter full name"
                      required
                    />
                    {error?.full_name && (
                      <p className="text-red-600 text-sm mt-1">{error?.full_name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="email"
                        value={formData?.email}
                        onChange={(e) => handleChange('email', e?.target?.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter email address"
                        required
                      />
                    </div>
                    {error?.email && (
                      <p className="text-red-600 text-sm mt-1">{error?.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="tel"
                        value={formData?.phone}
                        onChange={(e) => handleChange('phone', e?.target?.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact
                    </label>
                    <input
                      type="text"
                      value={formData?.emergency_contact}
                      onChange={(e) => handleChange('emergency_contact', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Emergency contact information"
                    />
                  </div>
                </div>
              </div>

              {/* Password Fields */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Account Security</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      value={formData?.password}
                      onChange={(e) => handleChange('password', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter password"
                      required
                    />
                    {error?.password && (
                      <p className="text-red-600 text-sm mt-1">{error?.password}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      value={formData?.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Confirm password"
                      required
                    />
                    {error?.confirmPassword && (
                      <p className="text-red-600 text-sm mt-1">{error?.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Job Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Job Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Staff Role *
                    </label>
                    <select
                      name="staff_role"
                      value={formData?.staff_role}
                      onChange={(e) => handleChange('staff_role', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      {staffRoleOptions?.map(option => (
                        <option key={option?.value} value={option?.value}>
                          {option?.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Default: Check-in Staff - Responsible for attendee registration and entry management
                    </p>
                    {error?.staff_role && (
                      <p className="text-red-600 text-sm mt-1">{error?.staff_role}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hourly Rate ($)
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData?.hourly_rate}
                        onChange={(e) => handleChange('hourly_rate', e?.target?.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hire Date
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="date"
                        value={formData?.hire_date}
                        onChange={(e) => handleChange('hire_date', e?.target?.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={formData?.status}
                      onChange={(e) => handleChange('status', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Skills & Qualifications</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Skills
                    </label>
                    
                    {/* Selected Skills */}
                    {formData?.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {formData?.skills?.map((skill) => (
                          <span
                            key={skill}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                          >
                            {skill?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                            <button
                              type="button"
                              onClick={() => removeSkill(skill)}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Available Skills */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {availableSkills?.filter(skill => !formData?.skills?.includes(skill))?.map((skill) => (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => addSkill(skill)}
                          className="px-3 py-1 text-sm border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                        >
                          {skill?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                        </button>
                      ))}
                    </div>
                    
                    {/* Custom Skill Input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={skillInput}
                        onChange={(e) => setSkillInput(e?.target?.value)}
                        placeholder="Add custom skill"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), handleAddCustomSkill())}
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomSkill}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                      <textarea
                        value={formData?.notes}
                        onChange={(e) => handleChange('notes', e?.target?.value)}
                        rows={3}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Additional notes about the staff member..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Adding...' : 'Add Staff Member'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AddStaffModal;